/**
 * This store module is the home of everything related to the Payment Service.  It can be used for
 * viewing credits, subscriptions, orders, and more.
 */

export const state = () => ({
  /**
   * Credits are any credits that are currently active and redeemable.
   */
  credits: [],

  /**
   * Subscriptions are the users currenctly active subscriptions.  Generally, there will
   * only be a single Subscription active at a time.
   *
   * TODO: Call this account plans
   */
  subscriptions: [],
})

export const mutations = {
  /**
   * Set the subscriptions list to a list of Logo.com API subscriptions.
   */
  setSubscriptions(state, subscriptions) {
    state.subscriptions = subscriptions
  },

  /**
   * Set the list of available credits to the provided value.  This should be used when fetching the list from the API,
   * or resetting the list.
   */
  setCredits(state, credits) {
    state.credits = credits
  },

  /**
   * Removes a Credit from the list of currently available credits.  Note: this should
   * not be used when a credit is assigned to the cart.  This should only be used following successful
   * orders.
   */
  removeCredit(state, credit) {
    state.credits = state.credits.filter(c => c.id !== credit)
  },

  /**
   * Adds a Credit into the users list of available credits.
   */
  addCredit(state, credit) {
    state.credits.push(credit)
  },
}

export const actions = {
  /**
   * Grab the list of the users credits and assign them to the store.
   */
  async fetchCredits({ commit }) {
    try {
      const auth = await this.$getAuthInstance()
      const user = await auth.currentAuthenticatedUser()
      const { data } = await this.$api.get(
        `/api/v2/credits/redeemers/user_${user.attributes.sub}`,
        {}
      )

      commit('setCredits', data)

      return data
    } catch (error) {
      // user not logged in, user has no credits
      return null
    }
  },

  /**
   * Adds the given credit to a users account, updating the list of available credits in the process.
   */
  async assignCreditToUser({ commit }, credit) {
    let id = credit

    // If  for some reason we can't include cr_, such as with appsumo,
    // then lets auto apply that sucker
    if (!id.startsWith('cr_')) {
      id = `cr_${credit}`
    }

    const auth = await this.$getAuthInstance()
    const user = await auth.currentAuthenticatedUser()

    const { data } = await this.$api.patch(`/api/v2/credits/${id}`, {
      redeemable_by: `user_${user.attributes.sub}`,
    })

    commit('addCredit', data)

    return data
  },

  /**
   * Fetches the Users currently active subscriptions.
   */
  async fetchSubscriptions({ commit }) {
    let subscriptions = []

    const { data } = await this.$api.get(`/api/v2/account-plans`)

    subscriptions = data.data

    commit('setSubscriptions', subscriptions)
  },
}

export const getters = {
  /**
   * Find a credit for a specific Package.
   *
   * @param {object} pkg
   */
  getCreditForPackage: ({ credits }) => ({ code }) => {
    return (
      credits.find(({ redeemable_for }) => redeemable_for.code === code) || null
    )
  },

  /**
   * Returns the users account subscription.  If no account subscription is detected, null
   * is returned.
   *
   * TODO: Once user subs are migrated to new format, we should specifically check for a special flag.
   */
  accountSubscription: ({ subscriptions }) => subscriptions[0] || null,

  /**
   * Boolean flag to determine if the User has an active account subscription.
   */
  hasAccountSubscription: (_state, getters) => !!getters.accountSubscription,

  /**
   * Returns the users Agency subscription, if they have one.
   */
  agencySubscription: ({ subscriptions }) =>
    subscriptions.find(sub => sub.package.code.startsWith('agency')),
}
